import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, CheckSquare, Square } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useTableTools } from "@/lib/useTableTools";
import { exportCsv } from "@/lib/exportCsv";
import { BulkBar } from "@/components/BulkBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export function CrudResource({ title, subtitle, endpoint, testid, columns, fields, searchKeys, searchPlaceholder, bulk, rowActions, hideCreate, extraHeader }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [options, setOptions] = useState({});
  const [assignOptions, setAssignOptions] = useState([]);

  const tools = useTableTools(rows, searchKeys || columns.map((c) => c.key));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setRows(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const loadOptions = async () => {
    const next = {};
    for (const f of fields) {
      if (f.optionsEndpoint) {
        try { const { data } = await api.get(f.optionsEndpoint); next[f.name] = data; }
        catch { next[f.name] = []; }
      }
    }
    setOptions(next);
  };

  useEffect(() => {
    load(); loadOptions();
    if (bulk?.assignEndpoint) {
      api.get(bulk.assignEndpoint)
        .then(({ data }) => setAssignOptions(data.map((o) => ({ value: o.id, label: o.company_name || o.name }))))
        .catch(() => {});
    }
    /* eslint-disable-next-line */
  }, [endpoint]);

  const openCreate = () => {
    const init = {};
    fields.forEach((f) => { init[f.name] = f.type === "switch" ? true : ""; });
    setForm(init); setEditing(null); setOpen(true);
  };
  const openEdit = (row) => {
    const init = {};
    fields.forEach((f) => { init[f.name] = row[f.name] ?? (f.type === "switch" ? false : ""); });
    setForm(init); setEditing(row); setOpen(true);
  };

  const save = async () => {
    const payload = {};
    fields.forEach((f) => {
      let v = form[f.name];
      if (f.type === "number") v = v === "" ? null : Number(v);
      payload[f.name] = v === "" ? null : v;
    });
    try {
      if (editing) await api.put(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(t("common.save") + " ✓");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (row) => {
    if (!window.confirm(`${t("common.delete")}?`)) return;
    try { await api.delete(`${endpoint}/${row.id}`); load(); toast.success("✓"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onBulk = async (action, value) => {
    const ids = [...tools.selected];
    if (action === "export") {
      exportCsv(tools.selectedRows, columns, `${testid}.csv`);
      return;
    }
    if (action === "delete" && !window.confirm(`${t("common.delete")} ${ids.length}?`)) return;
    try {
      const { data } = await api.post(`${endpoint}/bulk`, { ids, action, value });
      toast.success(`✓ ${data.deleted ?? data.updated ?? 0}`);
      tools.clear(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const colCount = columns.length + 2;

  return (
    <div className="space-y-4" data-testid={`${testid}-page`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">{t(title)}</h1>
          {subtitle && <p className="text-sm text-slate-500 mt-1">{t(subtitle)}</p>}
        </div>
        {extraHeader}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            {hideCreate ? <span /> : (
            <Button onClick={openCreate} data-testid={`${testid}-create-btn`} className="bg-blue-900 hover:bg-blue-800">
              <Plus size={16} className="mr-1.5" /> {t("common.create")}
            </Button>
            )}
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display">{editing ? t("common.edit") : t("common.create")} — {t(title)}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {fields.map((f) => (
                <div key={f.name} className="space-y-1.5">
                  <Label>{t(f.label)}</Label>
                  {f.type === "switch" ? (
                    <div><Switch checked={!!form[f.name]} onCheckedChange={(v) => setForm({ ...form, [f.name]: v })} data-testid={`field-${f.name}`} /></div>
                  ) : f.type === "select" || f.optionsEndpoint ? (
                    <Select value={form[f.name] ? String(form[f.name]) : ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                      <SelectTrigger data-testid={`field-${f.name}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        {(f.options || (options[f.name] || []).map((o) => ({ value: o.id, label: o[f.labelKey || "name"] || o.company_name || o.plate }))).map((o) => (
                          <SelectItem key={o.value} value={String(o.value)}>{t(o.label)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      type={f.type === "number" ? "number" : "text"}
                      value={form[f.name] ?? ""}
                      onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                      data-testid={`field-${f.name}`}
                    />
                  )}
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
              <Button onClick={save} data-testid={`${testid}-save-btn`} className="bg-blue-900 hover:bg-blue-800">{t("common.save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search + selection controls */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={tools.query}
            onChange={(e) => tools.setQuery(e.target.value)}
            placeholder={searchPlaceholder ? t(searchPlaceholder) : t("common.search")}
            className="pl-9"
            data-testid={`${testid}-search`}
          />
        </div>
        <Button variant="outline" size="sm" onClick={tools.selectAllVisible} data-testid={`${testid}-select-all`}><CheckSquare size={15} className="mr-1.5" /> {t("common.selectAll")}</Button>
        <Button variant="outline" size="sm" onClick={tools.clear} data-testid={`${testid}-clear`}><Square size={15} className="mr-1.5" /> {t("common.deselect")}</Button>
        <span className="text-sm text-slate-400 ml-auto">{tools.filtered.length} / {rows.length}</span>
      </div>

      {bulk && tools.selected.size > 0 && (
        <BulkBar
          count={tools.selected.size}
          role={user?.role}
          actions={bulk.actions}
          statusOptions={bulk.statusOptions}
          assignOptions={assignOptions}
          onAction={onBulk}
          onClear={tools.clear}
        />
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-10">
                <Checkbox checked={tools.allVisibleSelected} onCheckedChange={(v) => v ? tools.selectAllVisible() : tools.clear()} data-testid={`${testid}-header-checkbox`} />
              </TableHead>
              {columns.map((c) => <TableHead key={c.key} className="text-xs uppercase tracking-wider text-slate-500">{t(c.label)}</TableHead>)}
              <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={colCount} className="text-center py-10 text-slate-400">{t("common.loading")}</TableCell></TableRow>
            ) : tools.filtered.length === 0 ? (
              <TableRow><TableCell colSpan={colCount} className="text-center py-10 text-slate-400">{t("common.none")}</TableCell></TableRow>
            ) : tools.filtered.map((row) => (
              <TableRow key={row.id} data-testid={`${testid}-row`} data-selected={tools.selected.has(row.id)}>
                <TableCell>
                  <Checkbox checked={tools.selected.has(row.id)} onCheckedChange={() => tools.toggle(row.id)} data-testid={`${testid}-row-checkbox`} />
                </TableCell>
                {columns.map((c) => <TableCell key={c.key} className="text-sm text-slate-700">{c.render ? c.render(row) : (row[c.key] ?? "—")}</TableCell>)}
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {rowActions && rowActions(row)}
                    <Button size="icon" variant="ghost" onClick={() => openEdit(row)} data-testid={`${testid}-edit-btn`}><Pencil size={15} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(row)} data-testid={`${testid}-delete-btn`} className="text-red-600"><Trash2 size={15} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
